import { useCallback, useState } from "react";
import type { Release } from "../api/types";
import { embedURL, play, primeAudio, stop, stopEmbed, usePlaying } from "./player";
import { ShopPlayer } from "./ShopPlayer";
import { OwnButtons } from "./OwnButtons";

type Props = {
  release: Release;
  onTags: (tag: string) => void;
  onGenre: (genre: string) => void;
  onStyle: (style: string) => void;
  onLabel: (label: string) => void;
  activeTags: string[];
  activeGenres: string[];
  activeStyles: string[];
  activeLabels: string[];
  onTracks: (r: Release) => void;
  onSpeed: (r: Release) => void;
  /** "+": put the whole record on a playlist. Opens the picker where clicked. */
  onAddToPlaylist: (r: Release, at: { x: number; y: number }) => void;
};

/**
 * A cover, the release name and artist, then the details, then a chip per shop
 * carrying the record with that shop's own price in its own currency, then the
 * shop's genre and Bandcamp's tags.
 *
 * Three kinds of chip are told apart by colour -- label pink, shop genre
 * violet, Bandcamp tag amber -- because they answer three different questions,
 * and clicking a chip of any colour adds it to its own filter.
 *
 * The player slot under the cover is Bandcamp's embed where Bandcamp streams
 * the record, and the shop's own clips where it does not (ShopPlayer) -- a
 * record that is not on Bandcamp, or is there with nothing streamable, was
 * still listed by a shop, and most shops play their own previews.
 */
export function Card({
  release: r, onTags, onGenre, onStyle, onLabel,
  activeTags, activeGenres, activeStyles, activeLabels, onTracks, onSpeed, onAddToPlaylist,
}: Props) {
  const playingKey = usePlaying();
  const isPlaying = playingKey === r.key;
  const bc = r.bandcamp;

  // Bandcamp plays the record where it can. Otherwise the shop the API
  // proposes: one with clips on file, or one nobody has asked -- in which
  // case pressing play is the asking, and a "none" answer takes the button
  // away for this card without waiting for the page to reload.
  const viaBandcamp = !!bc && bc.streamable;
  const [noShop, setNoShop] = useState(false);
  const shop = !viaBandcamp && !noShop ? r.preview : undefined;
  const onNone = useCallback(() => setNoShop(true), []);
  const shopAudio = !!shop && (!shop.checked || shop.tracks > shop.embeds);

  return (
    <div className="card">
      <div className="thumb">
        {r.art && <img loading="lazy" src={r.art} alt={`${r.title} cover`} />}
        <div className="tt">
          <div className="n" title={r.title}>{r.title}</div>
          <div className="a" title={r.artist}>{r.artist}</div>
        </div>
      </div>

      {viaBandcamp && (isPlaying ? (
        <iframe
          loading="lazy"
          src={embedURL(bc.albumId, bc.itemType)}
          title={`${r.rawName} on Bandcamp`}
        />
      ) : (
        <button className="play" onClick={() => play(r.key)}>
          ▶ Play on Bandcamp
        </button>
      ))}
      {/* The player outlives a "none" answer so the note can be read; only
          the button goes, and closing the player takes the rest. */}
      {!viaBandcamp && r.preview && isPlaying && (
        <ShopPlayer release={r} onNone={onNone} onClose={() => stopEmbed()} />
      )}
      {shop && !isPlaying && (
        <button className="play shop" onClick={() => { primeAudio(); play(r.key); }}
                title={shop.checked
                  ? `${shop.tracks} clip${shop.tracks === 1 ? "" : "s"} on the shop's own page`
                  : "not on Bandcamp -- ask the shops carrying it for their own preview"}>
          ▶ {shop.checked ? `Play from ${shop.label}` : "Play the shop's preview"}
        </button>
      )}

      <div className="meta">
        <div className="row">
          {r.date && (
            <span className={`tag date${r.date.kind === "expected" ? "" : " listed"}`}>
              {r.date.text}
            </span>
          )}
          {/* A pre-order whose expected date is not the date shown still says so. */}
          {r.preorder && r.date?.kind !== "expected" && (
            <span className="tag date">Pre-order</span>
          )}
          {r.label && (
            <span
              className={`tag lb${activeLabels.includes(r.label.toLowerCase()) ? " on" : ""}`}
              title="filter by label"
              onClick={() => onLabel(r.label!)}
            >
              {r.label}
            </span>
          )}
          {r.catno && <span className="tag">{r.catno}</span>}

          {(bc?.hasTracks || (shop?.checked && shop.tracks > shop.embeds)) && (
            <button className="tracks"
                    title={bc?.hasTracks ? "tracklist, lengths and BPM" : `the clips ${shop!.label} plays`}
                    onClick={() => onTracks(r)}>
              Tracks
            </button>
          )}
          {/* Every playable release gets one: the API mints a preview URL when
              the popup opens, so nothing expires -- and where Bandcamp has
              nothing, the shop's clip is an MP3 the fader can pitch just the
              same. */}
          {(bc || shopAudio) && (
            <button className="speed"
                    title="play a preview at ±16% and hear it pitched"
                    onClick={() => onSpeed(r)}>
              Speed test
            </button>
          )}

          {r.confidence && <Confidence kind={r.confidence.kind} value={r.confidence.value} />}

          {/* Mine: the heart, and the whole record onto a list. At the end of
              the row, after what the catalogue says about the record. */}
          <span className="own">
            <OwnButtons
              release={r.key}
              label={`${r.artist} – ${r.title}`.replace(/^ – /, "")}
              onAdd={(e) => onAddToPlaylist(r, { x: e.clientX, y: e.clientY })}
            />
          </span>
        </div>

        <div className="row">
          {/* A shop can carry one record twice -- the CD and the 2LP -- and
              then its two chips need the format to tell them apart. */}
          {r.shops.map((s, i) => {
            const twice = r.shops.some((o, j) => j !== i && o.site === s.site);
            return (
              <a key={`${s.site}-${i}`} className="shop" href={s.url || "#"} target="_blank"
                 rel="noopener" title={s.rawName || ""}>
                <b>{s.label}</b>
                {twice && s.format && <span className="f"> {s.format}</span>}
                {s.priceText && <span className="p"> {s.priceText}</span>}
              </a>
            );
          })}
        </div>

        {(r.siteTags.length > 0 || r.bcTags.length > 0 || r.discogsTags.length > 0) && (
          <div className="row">
            {r.siteTags.map((t) => (
              <span key={`g${t}`}
                    className={`tag sg${activeGenres.includes(t.toLowerCase()) ? " on" : ""}`}
                    title="filter by shop genre" onClick={() => onGenre(t)}>
                {t}
              </span>
            ))}
            {r.bcTags.map((t) => (
              <span key={`t${t}`}
                    className={`tag bc${activeTags.includes(t.toLowerCase()) ? " on" : ""}`}
                    title="filter by Bandcamp tag" onClick={() => onTags(t)}>
                {t}
              </span>
            ))}
            {/* Discogs' own classification. Shown whether or not the record is
                on Bandcamp -- for most of the catalogue it is the only genre
                word that was not chosen by a retailer. */}
            {r.discogsTags.map((t) => (
              <span key={`d${t}`}
                    className={`tag dc${activeStyles.includes(t.toLowerCase()) ? " on" : ""}`}
                    title="filter by Discogs style" onClick={() => onStyle(t)}>
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="links">
          {bc?.url ? (
            <a href={bc.url} target="_blank" rel="noopener">Bandcamp ↗</a>
          ) : (
            (r.artist || r.title) && (
              // Nothing on Bandcamp: one click to look elsewhere. A link for
              // the reader to follow, not a lookup done on their behalf --
              // SoundCloud's robots.txt disallows /search for every crawler.
              <a className="alt" title="search SoundCloud for this release"
                 href={`https://soundcloud.com/search?q=${encodeURIComponent(`${r.artist} ${r.title}`.trim())}`}
                 target="_blank" rel="noopener">SoundCloud ↗</a>
            )
          )}
          {r.discogs ? (
            <a className="alt" href={r.discogs.url} target="_blank" rel="noopener"
               title={`${r.discogs.title || "this release"} on Discogs${r.discogs.score ? ` (${r.discogs.score})` : ""}`}>
              Discogs ↗
            </a>
          ) : (
            !bc && (r.artist || r.title) && (
              <a className="alt" title="look this release up on Discogs"
                 href={`https://www.discogs.com/search/?type=release&q=${encodeURIComponent(`${r.artist} ${r.title}`.trim())}`}
                 target="_blank" rel="noopener">Discogs ↗</a>
            )
          )}
          <span className="raw" title="as listed by the shop">{r.rawName}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * One number, meaning one of three things -- which is why it is not just a
 * score printed on the card.
 */
function Confidence({ kind, value }: { kind: string; value: number }) {
  const v = value.toFixed(2);
  if (kind === "match") {
    return <span className="tag" title="match confidence">~{v}</span>;
  }
  if (kind === "closest") {
    return (
      <span className="tag" title="best Bandcamp candidate scored this, below the threshold">
        closest ~{v}
      </span>
    );
  }
  return (
    <span className="tag" title="matched, but below the trusted score">
      ~{v} below threshold
    </span>
  );
}

export { stop as stopPlaying };
